import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Block } from '../api/client.ts';
import { useExportDownload } from '../lib/use-export.ts';
import { t } from '../i18n/index.ts';
import { useWindowPeaks } from '../lib/use-window-peaks.ts';
import { useSpeechGaps } from '../lib/use-gaps.ts';
import { snapTarget, midpointOf } from '../lib/gaps.ts';
import { formatClock, formatClockSeconds, formatDuration } from '../lib/format.ts';
import {
  blockWindow, placeSegments, coveredSpans, segmentAt, isFullyCovered,
} from '../lib/timeline.ts';
import { Waveform, type Selection } from './Waveform.tsx';

/** How far the arrow keys skip, as in any media player. */
const SKIP_MS = 5_000;
/** Shorter than this is a slip of the finger, not a clip. */
const MIN_CLIP_MS = 200;
/** How far one press of a −1 / +1 button moves an edge. */
const NUDGE_MS = 1_000;
/** How much plays after a nudge: enough to hear where the edge now falls. */
const EDGE_PREVIEW_MS = 2_000;
/**
 * Where the half hour still being recorded opens: this far back from its
 * newest audio. Whoever opens it has usually just heard something on air.
 */
const LIVE_LOOKBACK_MS = 120_000;

/** Where listening starts when the editor opens. */
function openingPositionMs(block: Block): number | null {
  const spans = coveredSpans(block);
  const first = spans[0];
  const last = spans[spans.length - 1];
  if (!first || !last) return null;
  if (block.status !== 'recording') return first.fromMs;
  return Math.max(last.fromMs, last.toMs - LIVE_LOOKBACK_MS);
}

/** A clock time with seconds, for an instant held as epoch milliseconds. */
function clockAt(ms: number, timeZone: string): string {
  return formatClockSeconds(new Date(ms).toISOString(), timeZone);
}

interface Props {
  block: Block;
  streamId: number;
  timeZone: string;
  onClose: () => void;
}

/**
 * Listening and cutting, reduced to the fewest decisions (spec §10.2 screen 4).
 *
 * Play; press "start of clip" as the part begins and "end of clip" as it ends;
 * listen back; download. That is the whole screen. Exact times, file names,
 * formats, nudges and edge auditions were all taken off it: each helped someone
 * now and then, and every one of them slowed down an ordinary clip.
 *
 * The precision comes from the ear rather than the pointer. Edges are placed at
 * the playhead while the audio runs, and a press close to a pause lands in the
 * pause, so reacting a moment late does not clip a word.
 *
 * Everything is in absolute clock time (spec §9): a block with an outage shows
 * the hole and still lets the surviving parts be cut, and playback runs from
 * one segment's file into the next without the producer noticing a join.
 */
export function BlockEditor({ block, streamId, timeZone, onClose }: Props): React.ReactElement {
  const audioRef = useRef<HTMLAudioElement>(null);
  // Which file is loaded, so the source is only swapped when it must be.
  const loadedSegment = useRef<number | null>(null);
  // Where "listen to the clip" stops.
  const previewUntil = useRef<number | null>(null);

  const [positionMs, setPositionMs] = useState<number | null>(() => openingPositionMs(block));
  const [playing, setPlaying] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [waveformWidth, setWaveformWidth] = useState(0);
  const exporter = useExportDownload();
  // The clip last sent, so the download button does not re-arm on it.
  const [sentKey, setSentKey] = useState<string | null>(null);

  const bounds = useMemo(() => blockWindow(block), [block]);
  const placed = useMemo(() => placeSegments(block), [block]);
  // The whole half hour in one strip. The stored waveform already holds more
  // detail than that width can draw, so nothing is decoded to show it.
  const peaks = useWindowPeaks(placed, bounds, waveformWidth);
  const gaps = useSpeechGaps(placed);

  /** Puts the playhead at an instant, switching files if it crosses a join. */
  const seek = useCallback((atMs: number): void => {
    const audio = audioRef.current;
    const target = segmentAt(block, atMs);
    setPositionMs(atMs);
    if (!audio || !target) return;

    if (loadedSegment.current !== target.segment.id) {
      loadedSegment.current = target.segment.id;
      audio.src = api.audioUrl(target.segment.id);
      audio.load();
      const start = (): void => { audio.currentTime = target.offsetSec; };
      audio.addEventListener('loadedmetadata', start, { once: true });
    } else {
      audio.currentTime = target.offsetSec;
    }
  }, [block]);

  const togglePlay = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    previewUntil.current = null;
    if (audio.paused) {
      if (loadedSegment.current === null && positionMs !== null) seek(positionMs);
      void audio.play().catch(() => undefined);
    } else {
      audio.pause();
    }
  }, [positionMs, seek]);

  // Opening a half hour means wanting to hear it, so it starts playing. Only
  // once: this re-runs as the playhead moves and must not undo a pause.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    togglePlay();
  }, [togglePlay]);

  const playClip = useCallback((): void => {
    if (!selection) return;
    previewUntil.current = selection.toMs;
    seek(selection.fromMs);
    void audioRef.current?.play().catch(() => undefined);
  }, [selection, seek]);

  /**
   * Puts an edge of the clip at the playhead. A press near a pause lands in
   * the middle of it, clear of the words either side.
   */
  const mark = useCallback((edge: 'from' | 'to'): void => {
    if (positionMs === null) return;
    const pause = snapTarget(positionMs, gaps);
    const at = pause ? midpointOf(pause) : positionMs;
    setSelection((current) => {
      if (edge === 'from') {
        // A start past the current end begins a new clip rather than an inverted one.
        return { fromMs: at, toMs: current && current.toMs > at ? current.toMs : at };
      }
      // An end needs a start before it.
      if (!current || at <= current.fromMs) return current;
      return { fromMs: current.fromMs, toMs: at };
    });
  }, [positionMs, gaps]);

  /**
   * Moves one edge by a second, then plays the side of it that is kept — the
   * clip's first two seconds after moving the start, its last two after moving
   * the end — so the new position is heard without asking for it. Deliberate,
   * so no snapping: a nudge lands exactly where it was sent.
   */
  const nudge = useCallback((edge: 'from' | 'to', deltaMs: number): void => {
    if (!selection) return;
    const next = edge === 'from'
      ? {
        fromMs: Math.max(bounds.fromMs, Math.min(selection.fromMs + deltaMs, selection.toMs - MIN_CLIP_MS)),
        toMs: selection.toMs,
      }
      : {
        fromMs: selection.fromMs,
        toMs: Math.min(bounds.toMs, Math.max(selection.toMs + deltaMs, selection.fromMs + MIN_CLIP_MS)),
      };
    setSelection(next);

    const from = edge === 'from' ? next.fromMs : Math.max(next.fromMs, next.toMs - EDGE_PREVIEW_MS);
    const to = edge === 'from' ? Math.min(next.toMs, next.fromMs + EDGE_PREVIEW_MS) : next.toMs;
    previewUntil.current = to;
    seek(from);
    void audioRef.current?.play().catch(() => undefined);
  }, [selection, bounds, seek]);

  // Keyboard, for whoever wants it; nothing on screen depends on it. Ignored
  // while a field has focus.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      switch (event.key) {
        case ' ':
          event.preventDefault();
          togglePlay();
          break;
        case 'i': case 'I':
          event.preventDefault();
          mark('from');
          break;
        case 'o': case 'O':
          event.preventDefault();
          mark('to');
          break;
        case 'Escape':
          setSelection(null);
          break;
        case 'ArrowLeft': case 'ArrowRight':
          if (positionMs === null) return;
          event.preventDefault();
          // The time axis runs left to right whatever the page direction.
          seek(positionMs + (event.key === 'ArrowRight' ? SKIP_MS : -SKIP_MS));
          break;
        default:
          break;
      }
    }
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [togglePlay, mark, seek, positionMs]);

  const clip = selection !== null && selection.toMs - selection.fromMs >= MIN_CLIP_MS
    ? selection
    : null;
  const spansGap = clip !== null && !isFullyCovered(block, clip.fromMs, clip.toMs);
  const clipKey = clip ? `${clip.fromMs}-${clip.toMs}` : null;
  const busy = exporter.phase === 'sending' || exporter.phase === 'preparing';
  const downloadable = clip !== null && !spansGap && clipKey !== sentKey && !busy;

  async function download(): Promise<void> {
    if (!clip || !downloadable) return;
    setSentKey(clipKey);
    await exporter.run({
      streamId,
      fromMs: clip.fromMs,
      toMs: clip.toMs,
      format: 'mp3',
      label: null,
    });
  }

  return (
    <section className="editor">
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={(e) => {
          const target = placed.find((p) => p.segment.id === loadedSegment.current);
          if (!target) return;
          const at = target.fileStartMs + e.currentTarget.currentTime * 1000;
          setPositionMs(at);
          // Stop at the end of "listen to the clip" rather than running on.
          if (previewUntil.current !== null && at >= previewUntil.current) {
            previewUntil.current = null;
            e.currentTarget.pause();
            return;
          }
          // Follow the playhead across a join without the producer noticing.
          if (at >= target.toMs) {
            const next = segmentAt(block, target.toMs + 1);
            if (next) {
              const wasPlaying = !e.currentTarget.paused;
              seek(target.toMs + 1);
              if (wasPlaying) void e.currentTarget.play().catch(() => undefined);
            } else {
              e.currentTarget.pause();
            }
          }
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <header className="editor-head">
        <span className="editor-range m" dir="ltr">
          {formatClock(block.block_start_at, timeZone)} – {formatClock(block.block_end_at, timeZone)}
        </span>
        <span className="spacer" />
        <button type="button" onClick={onClose}>{t('action.close')}</button>
      </header>

      {/* The whole half hour. Clicking jumps there; dragging marks a clip by
          eye for anyone who prefers that to marking by ear. */}
      <Waveform
        block={block}
        window={bounds}
        placed={placed}
        peaks={peaks}
        positionMs={positionMs}
        selection={selection}
        onSelectionChange={setSelection}
        onSeek={seek}
        onWidthChange={setWaveformWidth}
        gaps={gaps}
      />

      <div className="player-bar">
        <button
          type="button"
          className="play"
          onClick={togglePlay}
          disabled={positionMs === null}
        >
          {playing ? t('action.pause') : t('action.play')}
        </button>
        <span className="now m" dir="ltr">
          {positionMs === null ? '' : clockAt(positionMs, timeZone)}
        </span>
        <span className="spacer" />
        <button
          type="button"
          className="mark"
          onClick={() => mark('from')}
          disabled={positionMs === null}
        >
          {t('editor.set_start')}
        </button>
        <button
          type="button"
          className="mark"
          onClick={() => mark('to')}
          disabled={positionMs === null || selection === null}
        >
          {t('editor.set_end')}
        </button>
      </div>

      <div className="clip-bar">
        {clip ? (
          <>
            {/* Each edge's time between a second earlier and a second later.
                Earlier sits on the left: the time axis runs left to right. */}
            <span className="clip-range" dir="ltr">
              <button
                type="button"
                className="nudge"
                onClick={() => nudge('from', -NUDGE_MS)}
                aria-label={t('editor.start_earlier')}
                title={t('editor.start_earlier')}
              >
                {t('time.seconds', { seconds: '−1' })}
              </button>
              <span className="clip-time m">{clockAt(clip.fromMs, timeZone)}</span>
              <button
                type="button"
                className="nudge"
                onClick={() => nudge('from', NUDGE_MS)}
                aria-label={t('editor.start_later')}
                title={t('editor.start_later')}
              >
                {t('time.seconds', { seconds: '+1' })}
              </button>

              <span className="clip-dash">–</span>

              <button
                type="button"
                className="nudge"
                onClick={() => nudge('to', -NUDGE_MS)}
                aria-label={t('editor.end_earlier')}
                title={t('editor.end_earlier')}
              >
                {t('time.seconds', { seconds: '−1' })}
              </button>
              <span className="clip-time m">{clockAt(clip.toMs, timeZone)}</span>
              <button
                type="button"
                className="nudge"
                onClick={() => nudge('to', NUDGE_MS)}
                aria-label={t('editor.end_later')}
                title={t('editor.end_later')}
              >
                {t('time.seconds', { seconds: '+1' })}
              </button>
            </span>
            <span className="clip-length m" dir="ltr">
              {formatDuration((clip.toMs - clip.fromMs) / 1000)}
            </span>
            <span className="spacer" />
            <button type="button" onClick={playClip} disabled={spansGap}>
              {t('action.play_selection')}
            </button>
            <button
              type="button"
              className="cta"
              onClick={() => void download()}
              disabled={!downloadable}
            >
              {busy ? t('editor.preparing') : t('action.cut_and_download')}
            </button>
          </>
        ) : (
          // What to press next: the only instruction this screen needs.
          <p className="editor-hint">
            {selection
              ? t('editor.waiting_end', { time: clockAt(selection.fromMs, timeZone) })
              : t('editor.no_selection')}
          </p>
        )}
      </div>

      {spansGap && <p className="editor-hint warn">{t('editor.selection_spans_gap')}</p>}
      {exporter.phase === 'done' && clipKey === sentKey && (
        <p className="editor-hint">{t('editor.download_started')}</p>
      )}
      {exporter.error && <p className="editor-hint warn" role="alert">{exporter.error}</p>}
    </section>
  );
}
